import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api.js";
import { projectNavLinks, shareStatus, type ProjectPage } from "./projectPages.js";

/**
 * Whether this project's share is live, and how many times it was watched:
 * what the project nav says beside Export & share and Views. One read of the
 * share's settings, which carry the all-time count. Null until it is known,
 * and when it cannot be.
 */
export function useShareStatus(projectId: string) {
  const [status, setStatus] = useState<{ published: boolean; views: number } | null>(null);
  useEffect(() => {
    let current = true;
    api
      .share(projectId)
      .then((share) => {
        if (current) setStatus({ published: share.published, views: share.views });
      })
      .catch(() => {
        if (current) setStatus(null);
      });
    return () => {
      current = false;
    };
  }, [projectId]);
  return status;
}

/**
 * Edit · Export & share · Views: the three places a project lives, one click
 * apart from each other, the same for a product video and a launch film.
 * Export & share says Live or Draft — Draft in orange, because a video
 * somebody thinks is shared and is not is the thing worth noticing.
 */
export const ProjectNav: React.FC<{
  projectId: string;
  kind: "demo" | "launch";
  current: ProjectPage;
  /** Live or not, when the page knows better than the nav's own read (it has just published). */
  published?: boolean | null;
  /** Run before leaving this page through the nav: the editor saves first. */
  beforeLeave?: () => Promise<unknown>;
}> = ({ projectId, kind, current, published, beforeLeave }) => {
  const navigate = useNavigate();
  const known = useShareStatus(projectId);
  const badge = shareStatus(published ?? known?.published ?? null);

  return (
    <nav aria-label="Project" className="inline-flex shrink-0 gap-0.5 rounded-[10px] bg-bay-850 p-[3px]">
      {projectNavLinks(projectId, kind).map((link) => {
        const here = link.page === current;
        return (
          <Link
            key={link.page}
            to={link.href}
            aria-current={here ? "page" : undefined}
            onClick={(event) => {
              if (here || !beforeLeave || event.metaKey || event.ctrlKey || event.shiftKey) return;
              event.preventDefault();
              void beforeLeave()
                .catch(() => undefined)
                .then(() => navigate(link.href));
            }}
            className={`inline-flex items-center gap-1.5 rounded-[8px] px-3 py-1.5 text-[13px] font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-[var(--edge-focus)] ${
              here ? "bg-white text-ink-100 shadow-[0_1px_2px_rgba(16,21,28,0.12)]" : "text-ink-300 hover:text-ink-100"
            }`}
          >
            {link.label}
            {link.page === "export" && badge ? (
              <span className={`rounded-full px-1.5 py-px text-[11px] ${badge.className}`}>{badge.label}</span>
            ) : null}
            {link.page === "views" && known && known.views > 0 ? (
              <span className="tnum rounded-full bg-bay-750 px-1.5 py-px text-[11px] text-ink-200">{known.views}</span>
            ) : null}
          </Link>
        );
      })}
    </nav>
  );
};
